import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Segmented,
  Statistic,
  Spin,
  Empty,
  Button,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { getFootfallReport } from "../network/api";
import InfoHint from "./common/InfoHint";

const { Title, Text } = Typography;

// Door-open duration below the threshold = "planned" (grab-and-go);
// at or above = "unplanned" (browsing). Kept in sync with the backend default.
const THRESHOLD_SEC = 15;

const PLANNED_COLOR = "#52c41a"; // green — quick, intended pick-up
const UNPLANNED_COLOR = "#1890ff"; // blue — longer browse
const BAR_COLOR = "#1890ff";

const GRANULARITIES = [
  { label: "Hourly", value: "hourly" },
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const Footfall = () => {
  // When routed as /dashboard/footfall/:deviceId the view is scoped to one
  // device; otherwise it aggregates the whole fleet.
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [type, setType] = useState("daily");
  const [rows, setRows] = useState([]);
  const [thresholdSec, setThresholdSec] = useState(THRESHOLD_SEC);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFootfallReport({
        type,
        deviceIds: deviceId ? [deviceId] : null,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      if (res?.durationThresholdSec) setThresholdSec(res.durationThresholdSec);
    } catch (err) {
      console.error("Failed to load footfall report", err);
      message.error("Could not load footfall data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [type, deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.count += r.count || 0;
        acc.planned += r.plannedCount || 0;
        acc.unplanned += r.unplannedCount || 0;
        return acc;
      },
      { count: 0, planned: 0, unplanned: 0 }
    );
  }, [rows]);

  const donutData = useMemo(
    () => [
      { name: `Planned (< ${thresholdSec}s)`, value: totals.planned, color: PLANNED_COLOR },
      { name: `Unplanned (≥ ${thresholdSec}s)`, value: totals.unplanned, color: UNPLANNED_COLOR },
    ],
    [totals, thresholdSec]
  );

  const split = totals.planned + totals.unplanned;
  const plannedPct = split ? Math.round((totals.planned / split) * 100) : 0;
  const unplannedPct = split ? 100 - plannedPct : 0;
  const hasData = totals.count > 0;

  return (
    <div style={{ padding: 16 }}>
      {deviceId && (
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/dashboard/device-details/${deviceId}`)}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          Back to device
        </Button>
      )}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Footfall
            <InfoHint
              iconStyle={{ fontSize: 15 }}
              title={`Counts how often the chiller/cooler door is opened, inferred from the camera. Each door-open is one interaction. Use the Hourly/Daily/Weekly/Monthly toggle to change the time grouping. "Planned" opens are shorter than ${thresholdSec}s (a quick grab-and-go); "Unplanned" opens are longer (browsing).`}
            />
          </Title>
          <Text type="secondary">
            {deviceId
              ? `Door-open activity · Device ${deviceId}`
              : "Door-open activity across the fleet"}
          </Text>
        </Col>
        <Col>
          <Segmented
            options={GRANULARITIES}
            value={type}
            onChange={setType}
          />
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={<span>Total Door-Opens<InfoHint title="Total number of times the door was opened in the selected period — the overall footfall count." /></span>}
                value={totals.count}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={<span>{`Planned (< ${thresholdSec}s)`}<InfoHint title={`Share of door-opens shorter than ${thresholdSec} seconds — quick grab-and-go pick-ups where the customer already knew what they wanted.`} /></span>}
                value={plannedPct}
                suffix="%"
                valueStyle={{ color: PLANNED_COLOR }}
              />
              <Text type="secondary">{totals.planned} opens</Text>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={<span>{`Unplanned (≥ ${thresholdSec}s)`}<InfoHint title={`Share of door-opens ${thresholdSec} seconds or longer — the customer lingered/browsed, suggesting a less pre-planned purchase.`} /></span>}
                value={unplannedPct}
                suffix="%"
                valueStyle={{ color: UNPLANNED_COLOR }}
              />
              <Text type="secondary">{totals.unplanned} opens</Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={15}>
            <Card title={<span>Footfall Over Time<InfoHint title="Door-opens plotted across the selected time grouping. Use it to spot busy hours, days or seasonal trends." /></span>} style={{ height: "100%" }}>
              {hasData ? (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rows}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="range"
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar
                        dataKey="count"
                        name="Door-opens"
                        fill={BAR_COLOR}
                        radius={[3, 3, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty
                  description="No footfall in this range"
                  style={{ padding: "80px 0" }}
                />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <Card
              title={<span>Planned vs Unplanned Buying<InfoHint title={`Split of interactions by how long the door stayed open. Short opens (< ${thresholdSec}s) read as planned pick-ups; longer opens (≥ ${thresholdSec}s) read as unplanned browsing.`} /></span>}
              style={{ height: "100%" }}
            >
              {split > 0 ? (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {donutData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [`${value} opens`, name]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty
                  description="No footfall in this range"
                  style={{ padding: "80px 0" }}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Footfall;
